import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang, BEFORE_AFTER } from "../lib/i18n";

const Slider = ({ item, labels }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const down = (e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); update(e.clientX); };
  const move = (e) => { if (dragging.current) update(e.clientX); };
  const up = () => { dragging.current = false; };
  const key = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      className="relative aspect-[4/5] w-full overflow-hidden bg-[#ECE5DA] cursor-ew-resize select-none touch-pan-y"
    >
      <motion.img key={`a-${item.key}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }} src={item.after} alt={`${labels.after}`} draggable={false} className="absolute inset-0 w-full h-full object-cover object-[50%_30%]" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <motion.img key={`b-${item.key}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }} src={item.before} alt={`${labels.before}`} draggable={false} className="absolute inset-0 w-full h-full object-cover object-[50%_30%]" />
      </div>
      <span className="absolute top-5 left-5 overline text-[0.62rem] bg-[#14110F]/70 backdrop-blur-md text-[#F6F2EC] px-3 py-1.5 rounded-full" data-testid="before-label">{labels.before}</span>
      <span className="absolute top-5 right-5 overline text-[0.62rem] bg-[#F6F2EC]/80 backdrop-blur-md text-[#14110F] px-3 py-1.5 rounded-full" data-testid="after-label">{labels.after}</span>
      <div className="absolute inset-y-0 w-px bg-[#F6F2EC]" style={{ left: `${pos}%` }}>
        <button
          type="button"
          role="slider"
          aria-label={labels.drag}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          onKeyDown={key}
          data-testid="before-after-handle"
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#F6F2EC]/90 backdrop-blur-md border border-[#C9A66B] text-[#14110F] flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(20,17,15,0.5)] transition-transform duration-300 hover:scale-110"
        >
          <MoveHorizontal className="w-5 h-5" strokeWidth={1.4} />
        </button>
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  const { t, lang } = useLang();
  const b = t.beforeAfter;
  const [active, setActive] = useState(0);
  const item = BEFORE_AFTER[active];

  return (
    <section id="results" data-testid="before-after-section" className="bg-[#F6F2EC] py-24 sm:py-36">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <div className="lg:col-span-5">
          <Reveal><p className="overline text-[#8A6A3A] mb-6">{b.over}</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif-d font-light text-[2.6rem] sm:text-7xl leading-[0.95] text-[#14110F]">
              {b.title[0]} <em className="text-[#8A6A3A]">{b.title[1]}</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}><p className="mt-8 text-[#4f4841] font-light leading-relaxed max-w-md">{b.sub}</p></Reveal>
          <div className="mt-12 border-t border-[#ddd4c7]">
            {BEFORE_AFTER.map((x, i) => (
              <button
                key={x.key}
                data-testid={`before-after-tab-${x.key}`}
                onClick={() => setActive(i)}
                className="group w-full flex items-baseline gap-6 py-5 border-b border-[#ddd4c7] text-left"
              >
                <span className={`font-stencil tracking-[0.2em] text-sm transition-colors duration-500 ${i === active ? "text-[#8A6A3A]" : "text-[#8b8278]"}`}>0{i + 1}</span>
                <span className="flex-1">
                  <span className={`block font-serif-d text-3xl sm:text-4xl font-light transition-[color,transform] duration-500 ${i === active ? "text-[#14110F] translate-x-2" : "text-[#8b8278] group-hover:text-[#14110F]"}`}>{x[lang].t}</span>
                  <span className={`block text-sm mt-1 transition-opacity duration-500 ${i === active ? "text-[#5a534c] opacity-100" : "opacity-0 h-0 overflow-hidden"}`}>{x[lang].d}</span>
                </span>
              </button>
            ))}
          </div>
          <Reveal delay={0.1}><p className="mt-8 text-xs text-[#6b625a]" data-testid="before-after-disclaimer">{b.disclaimer}</p></Reveal>
        </div>
        <Reveal className="lg:col-span-6 lg:col-start-7">
          <Slider item={item} labels={b} />
          <p className="mt-4 flex items-center gap-3 overline text-[0.62rem] text-[#5a534c]"><MoveHorizontal className="w-4 h-4" strokeWidth={1.3} /> {b.drag}</p>
        </Reveal>
      </div>
    </section>
  );
};
